/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  FilesetResolver,
  FaceLandmarker,
  FaceLandmarkerResult,
  NormalizedLandmark,
} from '@mediapipe/tasks-vision'

// Declare cv as global if loaded via script tag
declare const cv: any

// Props for our component
interface FaceDetectionOverlayProps {
  videoRef: React.RefObject<HTMLVideoElement>
  isActive: boolean
  onGazeUpdate?: (isLookingAway: boolean) => void
  onFaceDetectionUpdate?: (detected: boolean, numFaces?: number) => void
}

// --- Constants for 3D Model and Landmarks ---
const MODEL_POINTS_3D = [
  [0.0, 0.0, 0.0], // Nose tip (origin)
  [0.0, -63.6, -12.5], // Chin
  [-43.3, 32.7, -26.0], // Left eye left corner (model's left)
  [43.3, 32.7, -26.0], // Right eye right corner (model's right)
  [-28.9, -28.9, -24.1], // Left Mouth corner (model's left)
  [28.9, -28.9, -24.1], // Right mouth corner (model's right)
]

// Landmark indices from MediaPipe corresponding to the order in MODEL_POINTS_3D
const MODEL_POINT_LANDMARK_INDICES = [
  4, // Nose tip
  152, // Chin
  263, // User's Left eye outer corner (Model's Left)
  33, // User's Right eye outer corner (Model's Right)
  287, // User's Left Mouth corner (Model's Left)
  57, // User's Right mouth corner (Model's Right)
]

// Pupil landmark indices (relative to user)
// Using user's LEFT eye (model's RIGHT eye)
const PUPIL_INDEX = 468

// Eyeball center in 3D model space
const EYE_BALL_CENTER_MODEL = [29.05, 32.7, -39.5]

// --- Constants for Gaze Angle Approach ---
// Define assumed "straight ahead" vector in MODEL space
const GAZE_AXIS_MODEL = [0.0, 0.0, 1.0]
// Angle tolerance in degrees
const GAZE_ANGLE_TOLERANCE_DEG = 32 // Degrees - TUNE THIS VALUE
const GAZE_ANGLE_TOLERANCE_RAD = (GAZE_ANGLE_TOLERANCE_DEG * Math.PI) / 180

// --- Global variable to hold resolved OpenCV ---
let cv2: any

// --- Vector Math Helper Functions ---
function dotProduct(v1: number[], v2: number[]): number {
  if (v1.length !== 3 || v2.length !== 3) return 0
  return v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2]
}

function magnitude(v: number[]): number {
  if (v.length !== 3) return 0
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2])
}

function normalize(v: number[]): number[] {
  if (v.length !== 3) return [0, 0, 0]
  const mag = magnitude(v)
  if (mag < 1e-9) return [0, 0, 0]
  return [v[0] / mag, v[1] / mag, v[2] / mag]
}
// --- End Helper Functions ---

const FaceDetectionOverlay: React.FC<FaceDetectionOverlayProps> = ({
  videoRef,
  isActive,
  onGazeUpdate,
  onFaceDetectionUpdate,
}) => {
  // ---------------------- State Variables ----------------------
  const [isLoadingMp, setIsLoadingMp] = useState(true)
  const [loadingMpError, setLoadingMpError] = useState<string | null>(null)
  const [mediaPipeLoaded, setMediaPipeLoaded] = useState(false)
  const [isOpenCvLoaded, setIsOpenCvLoaded] = useState(false)
  const lastIsLookingAway = useRef<boolean | null>(null)

  // ---------------------- Refs ----------------------
  const faceLandmarkerRef = useRef<FaceLandmarker | null>(null)
  const loadingMpTimeoutRef = useRef<number | null>(null)
  const lastVideoTimeRef = useRef<number>(-1)
  const requestAnimationRef = useRef<number | null>(null)

  // ---------------------- OpenCV Loading Effect ----------------------
  useEffect(() => {
    if (isOpenCvLoaded) return

    const checkCv = async () => {
      try {
        if (typeof cv === 'undefined' || cv === null) {
          setTimeout(checkCv, 500)
          return
        }
        if (cv instanceof Promise) {
          // Promise-based load (Emscripten factory): resolves only after WASM is
          // fully initialized, so we can mark ready immediately on resolution.
          cv2 = await cv
          if (cv2) {
            setIsOpenCvLoaded(true)
          } else {
            Sentry.captureMessage('OpenCV: Promise resolved to unusable value', 'warning')
          }
        } else {
          cv2 = cv
          if (cv2) {
            // Set the callback before checking flags to avoid a race where WASM
            // finishes between the check and the assignment.
            cv2.onRuntimeInitialized = () => {
              setIsOpenCvLoaded(true)
            }
            if (cv2.calledRun || cv2.runtimeInitialized) {
              setIsOpenCvLoaded(true)
            }
          } else {
            Sentry.captureMessage('OpenCV: resolved to undefined/unusable state', 'warning')
          }
        }
      } catch (err) {
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { event: 'opencv_init_error' },
        })
      }
    }

    const loadingTimeout = setTimeout(() => {
      if (!isOpenCvLoaded) {
        Sentry.captureMessage('OpenCV: loading timed out — gaze tracking disabled', 'warning')
      }
    }, 60000)

    if (document.readyState === 'complete') {
      checkCv()
    } else {
      window.addEventListener('load', checkCv)
    }

    return () => {
      clearTimeout(loadingTimeout)
      window.removeEventListener('load', checkCv)
    }
  }, [isOpenCvLoaded])

  // ---------------------- Load MediaPipe Tasks API ----------------------
  useEffect(() => {
    if (!isActive || mediaPipeLoaded || (isLoadingMp && loadingMpTimeoutRef.current !== null)) {
      return
    }
    setIsLoadingMp(true)
    setLoadingMpError(null)
    if (
      !videoRef.current ||
      !videoRef.current.srcObject ||
      videoRef.current.readyState < videoRef.current.HAVE_METADATA
    ) {
      console.warn('MediaPipe init: Video stream not ready.')
      setIsLoadingMp(false)
      return
    }
    loadingMpTimeoutRef.current = window.setTimeout(() => {}, 10000)
    const initializeMediaPipe = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        )
        const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numFaces: 2,
          minFaceDetectionConfidence: 0.5,
          minFacePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
          outputFacialTransformationMatrixes: false,
          outputFaceBlendshapes: false,
        })
        faceLandmarkerRef.current = faceLandmarker
        setMediaPipeLoaded(true)
        setIsLoadingMp(false)
        if (loadingMpTimeoutRef.current !== null) {
          window.clearTimeout(loadingMpTimeoutRef.current)
          loadingMpTimeoutRef.current = null
        }
        startDetectionLoop()
      } catch (err) {
        console.error('Error initializing MediaPipe:', err)
        setLoadingMpError(err.toString())
        setIsLoadingMp(false)
        if (loadingMpTimeoutRef.current !== null) {
          window.clearTimeout(loadingMpTimeoutRef.current)
          loadingMpTimeoutRef.current = null
        }
      }
    }
    initializeMediaPipe()
    return () => {}
  }, [isActive, videoRef, mediaPipeLoaded, isLoadingMp])

  useEffect(() => {
    if (
      isActive &&
      !mediaPipeLoaded &&
      !isLoadingMp &&
      videoRef.current?.srcObject &&
      videoRef.current?.readyState >= videoRef.current.HAVE_METADATA
    ) {
      setMediaPipeLoaded(false)
      setIsLoadingMp(true)
    }
  }, [
    isActive,
    mediaPipeLoaded,
    isLoadingMp,
    videoRef,
    videoRef.current?.srcObject,
    videoRef.current?.readyState,
  ])

  // ---------------------- Detection Loop ----------------------
  const startDetectionLoop = useCallback(() => {
    if (
      !faceLandmarkerRef.current ||
      !videoRef.current ||
      requestAnimationRef.current ||
      !isOpenCvLoaded
    ) {
      return
    }
    lastVideoTimeRef.current = -1
    const detectFaces = () => {
      if (!isActive || !faceLandmarkerRef.current || !isOpenCvLoaded) {
        stopDetectionLoop()
        return
      }
      const videoEl = videoRef.current
      if (
        !videoEl ||
        videoEl.paused ||
        videoEl.ended ||
        videoEl.readyState < videoEl.HAVE_CURRENT_DATA
      ) {
        requestAnimationRef.current = window.requestAnimationFrame(detectFaces)
        return
      }

      const startTimeMs = performance.now()
      const currentTime = videoEl.currentTime

      if (currentTime > 0 && currentTime !== lastVideoTimeRef.current) {
        lastVideoTimeRef.current = currentTime
        try {
          const results = faceLandmarkerRef.current.detectForVideo(videoEl, startTimeMs)
          processResults(results)
        } catch (error) {
          console.error('Error during detectForVideo:', error)
        }
      }
      requestAnimationRef.current = window.requestAnimationFrame(detectFaces)
    }
    requestAnimationRef.current = window.requestAnimationFrame(detectFaces)
  }, [videoRef, isActive, isOpenCvLoaded])

  const stopDetectionLoop = useCallback(() => {
    if (requestAnimationRef.current) {
      window.cancelAnimationFrame(requestAnimationRef.current)
      requestAnimationRef.current = null
    }
    lastVideoTimeRef.current = -1
  }, [])

  // ---------------------- Helper Function ----------------------
  function getPixelPoint(
    landmark: NormalizedLandmark | undefined | null,
    videoWidth: number,
    videoHeight: number
  ): { x: number; y: number } | null {
    if (!landmark) return null
    return {
      x: landmark.x * videoWidth,
      y: landmark.y * videoHeight,
    }
  }

  // ---------------------- Process Results (OpenCV - Angle Comparison Version) ----------------------
  const processResults = (results: FaceLandmarkerResult) => {
    const videoEl = videoRef.current
    if (!cv2 || !isOpenCvLoaded || !videoEl || videoEl.readyState < videoEl.HAVE_METADATA) {
      return
    }

    const faceCount = results.faceLandmarks?.length || 0
    const faceDetected = faceCount > 0

    // DEBUG
    // console.log('number of landmarks')
    // console.log(results.faceLandmarks.length)
    // console.log(results.faceLandmarks)

    if (onFaceDetectionUpdate) {
      onFaceDetectionUpdate(faceDetected, faceCount)
    }

    const landmarks = faceDetected ? results.faceLandmarks[0] : null
    const essentialLandmarksPresent =
      landmarks &&
      landmarks[PUPIL_INDEX] &&
      MODEL_POINT_LANDMARK_INDICES.every((index) => landmarks[index])

    if (!landmarks || !essentialLandmarksPresent) {
      const lookingAway = true
      if (lastIsLookingAway.current !== lookingAway) {
        if (onGazeUpdate) onGazeUpdate(lookingAway)
        lastIsLookingAway.current = lookingAway
      }
      return
    }

    const videoWidth = videoEl.videoWidth
    const videoHeight = videoEl.videoHeight

    // --- Prepare data for OpenCV ---
    const imagePoints_2D: number[][] = []
    for (const index of MODEL_POINT_LANDMARK_INDICES) {
      const point = getPixelPoint(landmarks[index], videoWidth, videoHeight)
      if (!point) {
        console.warn(`Landmark ${index} missing unexpectedly.`)
        if (onGazeUpdate) onGazeUpdate(true)
        lastIsLookingAway.current = true
        return
      }
      imagePoints_2D.push([point.x, point.y])
    }
    const pupilPoint_2D = getPixelPoint(landmarks[PUPIL_INDEX], videoWidth, videoHeight)
    if (!pupilPoint_2D) {
      console.warn(`Pupil landmark ${PUPIL_INDEX} missing unexpectedly.`)
      if (onGazeUpdate) onGazeUpdate(true)
      lastIsLookingAway.current = true
      return
    }

    // --- OpenCV Mats Initialization ---
    let modelPointsMat: any = null,
      imagePointsMat: any = null
    let cameraMatrixMat: any = null,
      distCoeffsMat: any = null
    let rvec: any = null,
      tvec: any = null,
      rotationMatrix: any = null
    let gazeAxisModelMat: any = null,
      gazeAxisCameraMat: any = null

    try {
      // --- Create OpenCV Mats ---
      modelPointsMat = cv2.matFromArray(
        MODEL_POINTS_3D.length,
        3,
        cv2.CV_64F,
        MODEL_POINTS_3D.flat()
      )
      imagePointsMat = cv2.matFromArray(imagePoints_2D.length, 2, cv2.CV_64F, imagePoints_2D.flat())

      const focalLength = videoWidth
      const center = [videoWidth / 2, videoHeight / 2]
      cameraMatrixMat = cv2.matFromArray(3, 3, cv2.CV_64F, [
        focalLength,
        0,
        center[0],
        0,
        focalLength,
        center[1],
        0,
        0,
        1,
      ])
      distCoeffsMat = cv2.Mat.zeros(4, 1, cv2.CV_64F)
      rvec = new cv2.Mat()
      tvec = new cv2.Mat()
      rotationMatrix = new cv2.Mat()
      gazeAxisModelMat = cv2.matFromArray(3, 1, cv2.CV_64F, GAZE_AXIS_MODEL)
      gazeAxisCameraMat = new cv2.Mat()

      // --- SolvePnP ---
      const pnpSuccess = cv2.solvePnP(
        modelPointsMat,
        imagePointsMat,
        cameraMatrixMat,
        distCoeffsMat,
        rvec,
        tvec,
        false,
        cv2.SOLVEPNP_ITERATIVE
      )
      if (!pnpSuccess) throw new Error('solvePnP failed')

      // --- Get Rotation Matrix ---
      cv2.Rodrigues(rvec, rotationMatrix)
      if (rotationMatrix.empty()) throw new Error('Rodrigues conversion failed')

      // --- Rotate Gaze Axis to Camera Space ---
      cv2.gemm(rotationMatrix, gazeAxisModelMat, 1, new cv2.Mat(), 0, gazeAxisCameraMat, 0)
      if (gazeAxisCameraMat.empty()) throw new Error('Gaze axis rotation failed')

      const gazeAxisCamera = [
        gazeAxisCameraMat.doubleAt(0, 0),
        gazeAxisCameraMat.doubleAt(1, 0),
        gazeAxisCameraMat.doubleAt(2, 0),
      ]

      // --- Calculate Pupil Direction Vector from Camera ---
      const fx = cameraMatrixMat.doubleAt(0, 0)
      const fy = cameraMatrixMat.doubleAt(1, 1)
      const cx = cameraMatrixMat.doubleAt(0, 2)
      const cy = cameraMatrixMat.doubleAt(1, 2)

      const pupilDirCamera = [(pupilPoint_2D.x - cx) / fx, (pupilPoint_2D.y - cy) / fy, 1.0]

      // --- Calculate Angle Between Vectors ---
      const normGazeAxis = normalize(gazeAxisCamera)
      const normPupilDir = normalize(pupilDirCamera)

      if (magnitude(normGazeAxis) < 1e-9 || magnitude(normPupilDir) < 1e-9) {
        throw new Error('Cannot compare zero vectors for gaze angle')
      }

      const cosAngle = dotProduct(normGazeAxis, normPupilDir)
      const clampedCosAngle = Math.max(-1.0, Math.min(1.0, cosAngle))
      const angleRad = Math.acos(clampedCosAngle)

      // --- Determine if Looking Away ---
      const isLookingAway = angleRad > GAZE_ANGLE_TOLERANCE_RAD

      // --- Update Parent (Only if state changes) ---
      if (lastIsLookingAway.current !== isLookingAway) {
        if (onGazeUpdate) {
          onGazeUpdate(isLookingAway)
        }
        lastIsLookingAway.current = isLookingAway
      }
    } catch (error) {
      console.error('Error during OpenCV processing:', error)
      const lookingAway = true
      if (lastIsLookingAway.current !== lookingAway) {
        if (onGazeUpdate) onGazeUpdate(lookingAway)
        lastIsLookingAway.current = lookingAway
      }
    } finally {
      // --- CRITICAL: Clean up all allocated OpenCV Mats ---
      try {
        modelPointsMat?.delete()
        imagePointsMat?.delete()
        cameraMatrixMat?.delete()
        distCoeffsMat?.delete()
        rvec?.delete()
        tvec?.delete()
        rotationMatrix?.delete()
        gazeAxisModelMat?.delete()
        gazeAxisCameraMat?.delete()
      } catch (cleanupError) {
        console.error('Error during OpenCV cleanup:', cleanupError)
      }
    }
  } // End of processResults

  // ---------------------- Cleanup ----------------------
  useEffect(() => {
    return () => {
      stopDetectionLoop()
      if (loadingMpTimeoutRef.current !== null) window.clearTimeout(loadingMpTimeoutRef.current)
    }
  }, [stopDetectionLoop])

  // ---------------------- Render ----------------------
  const isLoading = isLoadingMp
  const loadingError = loadingMpError

  return (
    <>
      {/* Loading/Error Indicators */}
      {isActive && (isLoading || loadingError) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 z-10 pointer-events-none">
          <div className="bg-white p-4 rounded shadow text-center">
            {isLoading && !loadingError && (
              <p className="text-sm text-gray-600 animate-pulse">
                Yükleniyor...
              </p>
            )}
            {loadingError && <p className="text-sm text-red-600 font-medium">{loadingError}</p>}
          </div>
        </div>
      )}
    </>
  )
}

export default FaceDetectionOverlay
